import _ from 'lodash'
import pluralize from 'pluralize'
import { ChangeEvent, useState } from 'react'
import { useSelector } from 'react-redux'
import { FetchResult } from '@apollo/client'
import { Checkbox, Input, message } from 'antd'
import { InfoCircleOutlined, MailOutlined } from '@ant-design/icons'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { useGetSelectedShippingOptionAndIsFreightShipping } from '@Builder/QuoteDetailPage/QuoteDetailsSummaryCard/components/ShippingSection/logic'

import Box from '~/core/Box'
import Button from '~/core/Button'
import Text from '~/core/Text'
import Popover from '~/core/Popover'
import Tooltip from '~/core/Tooltip'
import logger from '~/lib/logger'
import getColor from '~/lib/getColor'
import EmailAddressesForm from '~/apps/consumer/components/EmailAddressesForm'
import TeamsEarlyAccessSurveyModal from '~/apps/consumer/components/TopNav/TeamsEarlyAccessSurveyModal'
import {
    PricingInput,
    ShareQuoteMutation,
    ShareQuoteMutationVariables,
    useShareQuoteMutation,
} from '~/gql/graphql'

import quoteDetailsForInviteAndMember from './query'
import { useGetCarrierAccountId, useGetIncludePdfInput } from './logic'

type ShareQuoteFormPropsType = {
    id: string
    isLocatedInAdminPage?: boolean
    isQuotePurchasable?: boolean
    isForwardToPurchaser?: boolean
    priceInput?: PricingInput
    userId?: number
}

const SHARE_QUOTE_PLACEMENT = 'share-quote-form'
const FORWARD_QUOTE = 'forward-quote'

const ShareQuoteForm = (props: ShareQuoteFormPropsType) => {
    const {
        id,
        isLocatedInAdminPage,
        isQuotePurchasable,
        isForwardToPurchaser,
        priceInput,
        userId,
    } = props

    const [buttonDisabled, setButtonDisabled] = useState<boolean>(true)
    const [buttonLoading, setButtonLoading] = useState<boolean>(false)
    const [error, setError] = useState<boolean>(false)
    const [emails, setEmails] = useState<string[]>([])
    const [note, setNote] = useState<string>('')
    const [clearForm, setClearForm] = useState<boolean>(false)
    const [includePdf, setIncludePdf] = useState<boolean>(
        isForwardToPurchaser || false,
    )
    const [surveyModalVisible, setSurveyModalVisible] = useState<boolean>(false)

    const { selectedAddressId, selectedShippingOption } =
        useGetSelectedShippingOptionAndIsFreightShipping({
            quoteId: id,
        })

    const carrierAccountId = useSelector(useGetCarrierAccountId)

    const includePdfInput = useGetIncludePdfInput({
        carrierAccountId,
        priceInput,
        selectedAddressId,
        selectedShippingOption,
    })

    const emailAttributes = {
        sendCopy: false,
        includePdf: includePdf,
        forwardToPurchaser: !!isForwardToPurchaser,
    }

    const getEmailSpecificInput = (email: string) => {
        return {
            recipient: email,
            senderId: isLocatedInAdminPage && userId ? userId : null,
            note: note,
        }
    }

    const [shareQuote] = useShareQuoteMutation()

    const handleShareQuoteResults = (
        results: (
            | FetchResult<
                  ShareQuoteMutation,
                  Record<string, any>,
                  Record<string, any>
              >
            | Error
        )[],
    ) => {
        const errors = _.filter(results, _.isError)
        const successCount = results.length - errors.length

        if (successCount > 0) {
            message.success(
                `Quote shared with ${successCount} ${pluralize(
                    'users',
                    successCount,
                )}`,
            )
            setClearForm(true)
            setButtonDisabled(true)
            setTimeout(() => setClearForm(false), 10)
        }

        if (!_.isEmpty(errors)) {
            const _error = `${errors.length} ${pluralize(
                'invites',
                errors.length,
            )} failed. Collaborator might already be invited.`

            message.error(
                `Failed to share quote with ${errors.length} ${pluralize(
                    'users',
                    errors.length,
                )}`,
            )
            logger.exception(_error, {
                extra: {
                    info: _error,
                },
            })
        }

        return null
    }

    const onShareQuote = async () => {
        if (error) {
            return null
        }
        setButtonLoading(true)
        const shareQuoteMutations = emails.map(email => {
            const variables: ShareQuoteMutationVariables = {
                quoteId: id,
                emailSpecificInput: getEmailSpecificInput(email),
                emailAttributes: emailAttributes,
                includePdfInput,
            }

            return shareQuote({
                variables,
                refetchQueries: [
                    {
                        query: quoteDetailsForInviteAndMember,
                        variables: {
                            id,
                            senderId:
                                isLocatedInAdminPage && userId ? userId : null,
                        },
                    },
                ],
            }).catch(err => {
                logger.exception(err, {
                    extra: {
                        info: `ShareQuoteModal:: failed to share quote with ${email}: `,
                        err,
                    },
                })
                return new Error(`Failed to share quote with ${email}: ${err}`)
            })
        })

        try {
            const results = await Promise.all(shareQuoteMutations)

            handleShareQuoteResults(results)
            setNote('')
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: 'ShareQuoteModal:: failed to share quote:  ',
                    err,
                },
            })
        } finally {
            setButtonLoading(false)
        }
    }

    const onHandleFormChange = (change: {
        error: boolean
        value: string[]
    }) => {
        setError(!!change.error)
        setButtonDisabled(!!change.error)
        if (!change.error) {
            setEmails(change.value)
        }
    }

    const onHandleNoteChange = (evt: ChangeEvent<HTMLTextAreaElement>) => {
        setNote(evt.target.value)
    }

    const onHandleIncludePdfCheckbox = (evt: CheckboxChangeEvent) =>
        setIncludePdf(evt.target.checked)

    return (
        <Box direction="col" paddingLeft="8px" paddingRight="8px">
            {surveyModalVisible && (
                <TeamsEarlyAccessSurveyModal
                    visible={surveyModalVisible}
                    onCancel={() => setSurveyModalVisible(false)}
                    fromShareModal
                />
            )}
            <Text
                color="blackAlpha65"
                fontSize="16px"
                fontWeight={500}
                style={{ marginBottom: '4px' }}
            >
                {isForwardToPurchaser
                    ? 'Send quote details directly to your purchasing team'
                    : 'Share quote details with new or existing users'}
            </Text>
            <Text
                color="blackAlpha45"
                style={{
                    marginBottom: '8px',
                }}
            >
                {isForwardToPurchaser
                    ? 'Forwarding will enable a purchaser to edit, share, download or purchase this quote'
                    : 'An email will be sent to recipients who can then edit, share, download, or purchase parts'}
            </Text>
            <Box direction="col" marginBottom="8px">
                <EmailAddressesForm
                    onFormChanged={onHandleFormChange}
                    hasError={error}
                    showLabel={false}
                    clearForm={clearForm}
                />
                <Input.TextArea
                    data-name={`${SHARE_QUOTE_PLACEMENT}-note`}
                    data-test-target={`${SHARE_QUOTE_PLACEMENT}-note`}
                    maxLength={400}
                    onChange={onHandleNoteChange}
                    placeholder="Include a note to your collaborator..."
                    rows={3}
                    style={{ marginBottom: '8px' }}
                    value={note}
                />
                <Box alignItems="center">
                    {isForwardToPurchaser ? (
                        <Button
                            data-test-target={`${FORWARD_QUOTE}-button`}
                            disabled={buttonLoading || buttonDisabled}
                            icon={<MailOutlined />}
                            name={`${FORWARD_QUOTE}-button`}
                            onClick={onShareQuote}
                            placement={FORWARD_QUOTE}
                            style={{ marginRight: '16px' }}
                            type="primary"
                        >
                            {buttonLoading ? 'Forwarding' : 'Forward quote'}
                        </Button>
                    ) : (
                        <Button
                            data-test-target={`${SHARE_QUOTE_PLACEMENT}-button`}
                            disabled={buttonLoading || buttonDisabled}
                            icon={<MailOutlined />}
                            name={`${SHARE_QUOTE_PLACEMENT}-button`}
                            onClick={onShareQuote}
                            placement={SHARE_QUOTE_PLACEMENT}
                            style={{ marginRight: '16px' }}
                            type="primary"
                        >
                            {buttonLoading ? 'Sharing' : 'Share quote'}
                        </Button>
                    )}
                    <Tooltip
                        title={
                            !isQuotePurchasable
                                ? 'Please make sure all parts are quoted to include an attached PDF'
                                : null
                        }
                    >
                        <Checkbox
                            onChange={onHandleIncludePdfCheckbox}
                            defaultChecked={isForwardToPurchaser}
                            disabled={!isQuotePurchasable}
                        >
                            Include quote PDF with email{' '}
                            <Popover
                                content={
                                    <Box>
                                        <InfoCircleOutlined
                                            style={{
                                                color: getColor('link'),
                                                marginTop: '4px',
                                                marginRight: '12px',
                                            }}
                                        />
                                        <Text
                                            color="blackAlpha65"
                                            fontWeight={300}
                                        >
                                            A shared quote PDF may contain
                                            names, phone numbers, or shipping
                                            information relevant to this quote.
                                        </Text>
                                    </Box>
                                }
                                overlayStyle={{
                                    width: '287px',
                                }}
                            >
                                <InfoCircleOutlined
                                    style={{ color: getColor('link') }}
                                />
                            </Popover>
                        </Checkbox>
                    </Tooltip>
                </Box>
            </Box>
            <Text color="blackAlpha45" fontSize="11px">
                {isForwardToPurchaser
                    ? 'Note: Recipients must have an account to join the quote. Invitations for new users will expire after 5 days.'
                    : 'Note: Users must have an account to join the quote. Invitations for new users will expire after 5 days.'}
            </Text>
        </Box>
    )
}

export default ShareQuoteForm
